// client/src/components/BulkClassify.jsx
// Manager-only screen to assign work classifications to existing tenders
import { useState, useEffect } from 'react';
import { api } from '../api';
import { X, CheckCircle, AlertTriangle, Save } from 'lucide-react';

export default function BulkClassify({ onClose }) {
  const [tenders, setTenders]               = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [assignments, setAssignments]        = useState({}); // { tender_id: classification_id }
  const [loading, setLoading]                = useState(true);
  const [saving, setSaving]                  = useState(false);
  const [saved, setSaved]                    = useState(false);
  const [error, setError]                    = useState('');
  const [filter, setFilter]                  = useState('unclassified'); // 'all' | 'unclassified'

  useEffect(() => {
    api.get('/api/admin/bulk-classify').then(data => {
      setTenders(data.tenders);
      setClassifications(data.classifications);
      // Pre-fill assignments from existing data
      const existing = {};
      data.tenders.forEach(t => {
        if (t.classification_id) existing[t.id] = t.classification_id;
      });
      setAssignments(existing);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const setAssignment = (tenderId, classId) => {
    setAssignments(a => ({ ...a, [tenderId]: classId ? Number(classId) : null }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = Object.entries(assignments).map(([tender_id, classification_id]) => ({
        tender_id: Number(tender_id),
        classification_id: classification_id || null,
      }));
      await api.patch('/api/admin/bulk-classify', { assignments: payload });
      setSaved(true);
    } catch(e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const displayed = filter === 'unclassified'
    ? tenders.filter(t => !assignments[t.id])
    : tenders;

  const unclassifiedCount = tenders.filter(t => !assignments[t.id]).length;
  const classifiedCount   = tenders.length - unclassifiedCount;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'24px 16px', overflowY:'auto' }}>
      <div style={{ background:'var(--card-bg)', borderRadius:16, width:'100%', maxWidth:900, boxShadow:'0 32px 80px rgba(0,0,0,0.6)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ color:'var(--text)', margin:0, fontSize:17, fontWeight:600 }}>Bulk Classification Assignment</h2>
            <p style={{ color:'var(--text-muted)', fontSize:12, margin:'4px 0 0' }}>
              Assign work classifications to existing tenders. Required for the classification charts to populate.
            </p>
          </div>
          <button onClick={onClose} style={{ background:'var(--surface2)', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:7, borderRadius:7, display:'flex' }}>
            <X size={16}/>
          </button>
        </div>

        {/* Stats bar */}
        <div style={{ padding:'14px 24px', borderBottom:'1px solid var(--border)', display:'flex', gap:20, alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
            <CheckCircle size={14} color="#10b981"/>
            <span style={{ color:'#10b981', fontSize:13, fontWeight:600 }}>{classifiedCount} classified</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
            <AlertTriangle size={14} color={unclassifiedCount > 0 ? '#eab308' : '#475569'}/>
            <span style={{ color: unclassifiedCount > 0 ? '#eab308' : '#475569', fontSize:13, fontWeight:600 }}>
              {unclassifiedCount} unclassified
            </span>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
            {['unclassified','all'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ padding:'5px 12px', border:`1px solid ${filter===f ? '#3b82f6' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius:7, background: filter===f ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: filter===f ? '#60a5fa' : '#64748b', fontSize:11, cursor:'pointer', textTransform:'capitalize' }}>
                {f === 'unclassified' ? `Unclassified (${unclassifiedCount})` : `All (${tenders.length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div style={{ padding:'0 24px', maxHeight:'60vh', overflowY:'auto' }}>
          {loading ? (
            <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Loading tenders…</div>
          ) : displayed.length === 0 ? (
            <div style={{ padding:40, textAlign:'center' }}>
              <CheckCircle size={32} color="#10b981" style={{ margin:'0 auto 12px', display:'block' }}/>
              <p style={{ color:'#10b981', fontSize:14, fontWeight:600, margin:0 }}>All tenders classified</p>
              <p style={{ color:'var(--text-muted)', fontSize:12, margin:'4px 0 0' }}>Switch to "All" to review or adjust.</p>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  {['Ref','Project','Client','Status','Est.','Classification'].map(h => (
                    <th key={h} style={{ padding:'10px 8px', color:'var(--text-dim)', fontSize:11, fontWeight:600, textAlign:'left', textTransform:'uppercase', letterSpacing:0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map(t => {
                  const assigned = assignments[t.id];
                  return (
                    <tr key={t.id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'9px 8px', color:'var(--text-muted)', fontSize:11, whiteSpace:'nowrap' }}>{t.project_ref || '—'}</td>
                      <td style={{ padding:'9px 8px', color:'var(--text)', fontSize:12, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.project_name}>{t.project_name}</td>
                      <td style={{ padding:'9px 8px', color:'var(--text-dim)', fontSize:11, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={t.client}>{t.client}</td>
                      <td style={{ padding:'9px 8px' }}>
                        <span style={{ fontSize:10, padding:'2px 7px', borderRadius:4, background:'var(--surface2)', color:'var(--text-dim)' }}>{t.status}</span>
                      </td>
                      <td style={{ padding:'9px 8px', color:'var(--text-muted)', fontSize:11 }}>{t.estimator || '—'}</td>
                      <td style={{ padding:'9px 8px' }}>
                        <select value={assigned || ''} onChange={e => setAssignment(t.id, e.target.value)}
                          style={{ width:'100%', minWidth:180, padding:'6px 10px',
                            background: assigned ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${assigned ? 'rgba(16,185,129,0.25)' : 'rgba(255,99,71,0.3)'}`,
                            borderRadius:7, color: assigned ? '#10b981' : '#f87171', fontSize:11, cursor:'pointer' }}>
                          <option value="">— Select classification —</option>
                          {classifications.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 24px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            {error && <p style={{ color:'#f87171', fontSize:12, margin:0 }}>{error}</p>}
            {saved && (
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <CheckCircle size={13} color="#10b981"/>
                <span style={{ color:'#10b981', fontSize:12 }}>Saved successfully</span>
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={onClose}
              style={{ padding:'8px 16px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer' }}>
              Close
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'8px 20px', background: saving ? 'rgba(59,130,246,0.4)' : '#3b82f6', border:'none', borderRadius:8, color:'#fff', fontSize:13, fontWeight:600, cursor: saving ? 'default' : 'pointer', display:'flex', alignItems:'center', gap:7 }}>
              <Save size={13}/>{saving ? 'Saving…' : 'Save Classifications'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
